import {makeAutoObservable, runInAction} from "mobx";
import ProductCaseStore from "../product-case-store";
import RmaPageStore from "../rma-page-store";
import {
    CANT_REJECT_ITEM_REASONS,
    IActionReasonField,
    IEmailAttachment,
    IRmaActionStore,
    IRmaRejectItemRequest
} from "../../rma-types";

export enum REJECT_TYPE {
    REMOVE_FROM_REFUND = 'remove-from-refund',
    REJECT_ITEM = 'reject-item'
}

class RmaRejectItemActionStore implements IRmaActionStore {

    private _request: Partial<IRmaRejectItemRequest> = {};
    private _rejectType: REJECT_TYPE = REJECT_TYPE.REMOVE_FROM_REFUND;
    private _selectedProductCaseForReject: ProductCaseStore | undefined;
    private _isProcessing: boolean = false;

    constructor(private _rma: RmaPageStore, private _actionCallback: (data: IRmaRejectItemRequest) => void) {
        makeAutoObservable(this, {}, {autoBind: true});
    }

    public setProductCaseForReject(productCaseStore: ProductCaseStore): void {
        this._selectedProductCaseForReject = productCaseStore;
        this._request = {};
        this._rejectType = REJECT_TYPE.REMOVE_FROM_REFUND;
    }

    public shouldShowRejectPopup(productCaseStore: ProductCaseStore): boolean {
        return productCaseStore.canNotBeRejectedReason !== CANT_REJECT_ITEM_REASONS.FEATURE_IS_NOT_SUPPORTED &&
            productCaseStore.canNotBeRejectedReason !== CANT_REJECT_ITEM_REASONS.RMA_IS_NOT_IN_PENDING_APPROVAL
    }

    public getProductCaseForReject(): ProductCaseStore|undefined {
        return this._selectedProductCaseForReject;
    }

    public removeProductCaseForReject(): void {
        this._selectedProductCaseForReject = undefined;
    }

    public setSelectedRejectType(v: REJECT_TYPE): void {
        this._rejectType = v;
    }

    public getRejectType(): REJECT_TYPE {
        return this._rejectType;
    }

    public setItemRejectReason(v: string): void {
        this._request.reject_reason = v;
    }

    public getItemRejectReason(): string | null {
        return this._request.reject_reason ?? null;
    }

    get isProcessing(): boolean {
        return this._isProcessing;
    }

    private requestModel(): IRmaRejectItemRequest {
        return {
            reject_reason: this.getItemRejectReason(),
            product_case_id: this.getProductCaseForReject()?.id as number,
        }
    }

    public async submitAction(closeCb?: () => void): Promise<void> {

        if(this.getProductCaseForReject()?.isRejected) {
            return;
        }

        if (this._rejectType === REJECT_TYPE.REMOVE_FROM_REFUND) {
            this._selectedProductCaseForReject?.setIsChecked(false);
            this.removeProductCaseForReject();
            if (closeCb) {
                closeCb();
            }
            return;
        }

        if (this._rejectType === REJECT_TYPE.REJECT_ITEM) {

            if(!this._selectedProductCaseForReject?.canBeRejected) {
                console.log(`Item cannot be rejected due to: ${this._selectedProductCaseForReject?.rejectedReason}`);
                return;
            }

            try {
                this._isProcessing = true;
                await this._actionCallback(this.requestModel());
                runInAction(() => {
                    this._isProcessing = false;
                    if (closeCb) {
                        closeCb();
                    }
                });
            }
            catch (e) {
                runInAction(() => {
                    this._isProcessing = false;
                });
            }
        }
    }

    /** stub methods required by interface interface */
    public get reasonField(): IActionReasonField | undefined { return undefined; }
    public get newReturnMethodId(): number | undefined { return undefined; }
    public get returnMethodWarning(): string | null { return null; }
    public get showReturnMethodChangeWarning(): boolean { return false; }
    public get sendEmailNotification(): boolean { return false; }
    public get emailAdditionalContent(): string | undefined { return undefined; }
    public get emailAttachments(): IEmailAttachment[] { return []; }
    public get restockingLocationId(): string | undefined { return undefined; }
    public setEmailAdditionalContent(v: string) {}
    public addEmailAttachment(attachment: IEmailAttachment): void {}
    public removeEmailAttachment(attachment: IEmailAttachment): void {}
    public resetReturnMethodChange() {}
    public confirmReturnMethodChange() {}
    public confirmReturnMethodsChangeWithLabelFees() {}
    public confirmReturnMethodsChangeWithoutLabelFees() {}
    public hasCustomerEmail(): boolean { return false; }
    public setRestockingLocationId(v: string): void {}
    public setSelectedProductCaseIds(v: number[]): void {}
    public setSendEmailNotification(v: boolean): void {}
    public setNewReturnMethodId(v: number): void {}
}

export default RmaRejectItemActionStore;
